import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { BroadcastService } from '../../core/services/broadcast.service';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthenticatedUser } from './authenticated-user.model';
import { AuthApiService } from './auth-api.service';
import { LocalStorageManagerService } from '../../core/services/local-storage-manager.service';
import { UserRegistrationInfo } from './user-registration-info.model';

@Injectable({
    providedIn: 'root'
})
export class AuthManagerService
{
    private _authenticatedUser: AuthenticatedUser | undefined = undefined;
    public redirectUrl = '/section';

    constructor(private authApiService: AuthApiService,
                private router: Router,
                private broadcastService: BroadcastService,
                private localStorageManager: LocalStorageManagerService)
    {
        // Create the authenticatedUser from the last userLoginResult if it exists (i.e., the user has not logged out).
        const result = this.localStorageManager.userLoginResult;
        if (result !== undefined && new Date(result.RefreshTokenExpiration).getTime() > new Date().getTime())
        {
            this.authenticatedUser = new AuthenticatedUser(result);
        }
    }

    get authenticatedUser(): AuthenticatedUser | undefined
    {
        return this._authenticatedUser;
    }

    set authenticatedUser(value: AuthenticatedUser | undefined)
    {
        // if (value) console.log(`Setting authenticated user: ${value.fullName}`);
        this._authenticatedUser = value;
    }

    signIn(userName: string, password: string): Observable<AuthenticatedUser>
    {
        return this.authApiService.signIn(userName, password)
            .pipe(
                tap(authenticatedUser =>
                    {
                        this.authenticatedUser = authenticatedUser;
                        this.broadcastService.userSignedIn.next(this.authenticatedUser);
                    })
            );
    }

    signInWithRefreshToken(organizationId?: number)
    {
        const orgId = organizationId === undefined && this.authenticatedUser?.organizationId !== undefined ? +this.authenticatedUser.organizationId : organizationId;
        if (!this.localStorageManager.refreshToken || orgId === undefined)
        {
            return throwError(new HttpErrorResponse({status: 400, statusText: 'no refresh token found'}));
        }

        return this.authApiService.signInWithRefreshToken(this.localStorageManager.refreshToken, orgId)
            .pipe(
                tap((authenticatedUser: AuthenticatedUser) =>
                {
                    this.authenticatedUser = authenticatedUser;
                    this.broadcastService.userSignedIn.next(this.authenticatedUser);
                })
            );
    }

    signOut()
    {
        this.doSignOut();
    }

    private doSignOut()
    {
        this.localStorageManager.clearCacheOnSignOut();

        this.redirectUrl = '/section';

        this.broadcastService.userSignedOut.next(this.authenticatedUser!.sid);
        this.authenticatedUser = undefined;

        this.router.navigate(['/account/sign-in']);
    }

    changePassword(oldPassword: string, newPassword: string)
    {
        return this.authApiService.changePassword(oldPassword, newPassword);
    }

    forgotPassword(userName: string)
    {
        return this.authApiService.forgotPassword(userName);
    }

    resetPassword(userId: string, token: string, password: string)
    {
        return this.authApiService.resetPassword(userId, token, password);
    }

    registerNewUser(userRegistrationInfo: UserRegistrationInfo)
    {
        return this.authApiService.registerNewUser(userRegistrationInfo);
    }

    confirmEmail(confirmInfo: { userId: string, token: string })
    {
        return this.authApiService.confirmEmail(confirmInfo);
    }

    confirmUserEmail(email: string): Observable<any>
    {
        return this.authApiService.confirmUserEmail(email);
    }

    setUserPassword(email: string, password: string): Observable<any>
    {
        return this.authApiService.setUserPassword(email, password);
    }

    generateSupportCode(email: string): Observable<any>
    {
        return this.authApiService.generateSupportCode(email);
    }

    //
    // UI Support
    //
    public get showGlobalAdminOptions(): boolean { return this.authenticatedUser !== undefined && this.authenticatedUser.isGlobalAdmin; }
    public get showNews(): boolean { return this.hasAccess('News'); }
    public get showCheckInCheckOut(): boolean { return this.hasAccess('CheckInCheckOut'); }
    public get showEvents(): boolean { return this.hasAccess('Events'); }
    public get showNotifications(): boolean { return this.hasAccess('Notifications'); }
    public get showPhotos(): boolean { return this.hasAccess('Photos'); }
    public get showMembers(): boolean { return this.hasAccess('Members'); }
    public get showContacts(): boolean { return this.hasAccess('Contacts'); }
    public get showCategories(): boolean { return this.hasAccess('Categories'); }
    public get showDocCenter(): boolean { return this.hasAccess('DocumentCenter'); }
    public get showOrganizationInfo(): boolean { return this.hasAccess('OrganizationInfo'); }
    public get showLocations(): boolean { return this.hasAccess('Locations'); }
    public get showSponsors(): boolean { return this.hasAccess('Sponsors'); }
    public get showSocialMedia(): boolean { return this.hasAccess('OrganizationInfo'); }
    public get showRolloverWizard(): boolean { return this.hasAccess('OrganizationInfo'); }
    public get showThemes(): boolean { return this.showOrganizationInfo; }
    public get showReports(): boolean { return this.hasAccess('Reports'); }

	public get editGlobalAdmins(): boolean { return this.hasGlobalAdminAccess('EditGlobalAdmins'); }
	public get editThemes(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('Themes'); }
	public get editOrgSettings(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('OrgSettings'); }
	public get editOrgAdmins(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('OrgAdmins'); }
	public get createOrganizations(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('CreateOrg'); }
	public get viewFaceDetectionResults(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('FaceDetection'); }
	public get editAppStatuses(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('AppStatus'); }
	public get getClientReport(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('ClientReport'); }
	public get editGeneralSettings(): boolean { return this.editGlobalAdmins || this.hasGlobalAdminAccess('GeneralSettings'); }

	private hasAccess(propertyName: string): boolean { return this.authenticatedUser !== undefined &&
        (this.authenticatedUser.isGlobalAdmin || this.authenticatedUser.accessInfo === undefined || this.authenticatedUser.accessInfo[propertyName] === true); }

	private hasGlobalAdminAccess(propertyName: string): boolean
	{
		return this.authenticatedUser !== undefined && this.authenticatedUser.isGlobalAdmin &&
			(this.authenticatedUser.accessInfo === undefined || this.authenticatedUser.accessInfo[propertyName] === true);
	}
}
